import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { FileText, Search, Upload, X, CheckCircle } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

const CertificatesModal = ({ isOpen, onClose, instruments, onUpdateCertificates }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedInstrument, setSelectedInstrument] = useState('');
  const [certificates, setCertificates] = useState({
    certificadoCaltek: null,
    certificadoNeomatic: null,
    certificadoTecnocalibracao: null,
    certificadoCalibratools: null,
  });
  const { toast } = useToast();

  const filteredInstruments = instruments ? instruments.filter((inst) =>
    inst && inst.nome && typeof inst.nome === 'string' && inst.nome.toLowerCase().includes(searchTerm.toLowerCase())
  ) : [];

  useEffect(() => {
    if (selectedInstrument) {
      const instrument = instruments.find((inst) => inst.id === selectedInstrument);
      if (instrument) {
        setCertificates({
          certificadoCaltek: instrument.certificadoCaltek || null,
          certificadoNeomatic: instrument.certificadoNeomatic || null,
          certificadoTecnocalibracao: instrument.certificadoTecnocalibracao || null,
          certificadoCalibratools: instrument.certificadoCalibratools || null,
        });
      }
    }
  }, [selectedInstrument, instruments]);

  const handleFileUpload = (certificateType, e) => {
    const file = e.target.files[0];
    if (file) {
      // Validar se é PDF
      if (file.type !== 'application/pdf') {
        toast({
          title: 'Formato inválido',
          description: 'Por favor, selecione apenas arquivos PDF.',
          variant: 'destructive',
        });
        return;
      }

      // Validar tamanho (máx 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast({
          title: 'Arquivo muito grande',
          description: 'O certificado deve ter no máximo 5MB.',
          variant: 'destructive',
        });
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result;
        setCertificates({
          ...certificates,
          [certificateType]: {
            name: file.name,
            data: base64String,
            uploadDate: new Date().toISOString(),
          },
        });
        toast({
          title: 'Certificado carregado!',
          description: `${file.name} foi carregado com sucesso.`,
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveCertificate = (certificateType) => {
    setCertificates({
      ...certificates,
      [certificateType]: null,
    });
    toast({
      title: 'Certificado removido',
      description: 'O certificado foi removido.',
    });
  };

  const handleDownloadCertificate = (certificate) => {
    if (certificate && certificate.data) {
      const link = document.createElement('a');
      link.href = certificate.data;
      link.download = certificate.name;
      link.click();
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!selectedInstrument) {
      toast({
        title: 'Selecione um instrumento',
        description: 'Por favor, selecione um instrumento para atualizar os certificados.',
        variant: 'destructive',
      });
      return;
    }

    onUpdateCertificates(selectedInstrument, certificates);

    toast({
      title: 'Certificados atualizados!',
      description: 'Os certificados foram salvos com sucesso.',
    });

    handleClose();
  };

  const handleClose = () => {
    setSearchTerm('');
    setSelectedInstrument('');
    setCertificates({
      certificadoCaltek: null,
      certificadoNeomatic: null,
      certificadoTecnocalibracao: null,
      certificadoCalibratools: null,
    });
    onClose();
  };

  const renderCertificateField = (label, certificateType) => {
    const certificate = certificates[certificateType];

    return (
      <div className="space-y-2">
        <Label>{label}</Label>
        {certificate ? (
          <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-green-900 truncate">
                {certificate.name}
              </p>
              <p className="text-xs text-green-700">
                Enviado em {new Date(certificate.uploadDate).toLocaleDateString('pt-BR')}
              </p>
            </div>
            <div className="flex gap-1">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => handleDownloadCertificate(certificate)}
                className="h-8 px-2 text-green-700 hover:text-green-900 hover:bg-green-100"
              >
                <FileText className="w-4 h-4" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => handleRemoveCertificate(certificateType)}
                className="h-8 px-2 text-red-600 hover:text-red-900 hover:bg-red-50"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
        ) : (
          <Button
            type="button"
            variant="outline"
            onClick={() => document.getElementById(`${certificateType}-upload`).click()}
            className="w-full border-dashed border-2 border-slate-300 hover:border-orange-400 hover:bg-orange-50"
          >
            <Upload className="w-4 h-4 mr-2" />
            Fazer Upload do PDF
          </Button>
        )}
        <input
          id={`${certificateType}-upload`}
          type="file"
          accept=".pdf"
          onChange={(e) => handleFileUpload(certificateType, e)}
          className="hidden"
        />
      </div>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-2xl">
            <FileText className="w-6 h-6 text-blue-600" />
            Gerenciar Certificados
          </DialogTitle>
          <DialogDescription>
            Selecione um instrumento e faça upload dos certificados de calibração
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          {/* Instrument Selection */}
          <div className="space-y-2">
            <Label htmlFor="search">Buscar Instrumento</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                id="search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Digite o nome do instrumento..."
                className="pl-10"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="instrument">Selecione o Instrumento</Label>
            <Select
              value={selectedInstrument}
              onValueChange={setSelectedInstrument}
            >
              <SelectTrigger>
                <SelectValue placeholder="Escolha um instrumento" />
              </SelectTrigger>
              <SelectContent>
                {filteredInstruments.length === 0 ? (
                  <div className="p-2 text-sm text-slate-500 text-center">
                    Nenhum instrumento encontrado
                  </div>
                ) : (
                  filteredInstruments.map((inst) => (
                    <SelectItem key={inst.id} value={inst.id}>
                      {inst.nome} - {inst.marca}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>

          {/* Certificates Upload Section */}
          <div className="bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200 rounded-lg p-4 space-y-4">
            <div className="flex items-center gap-3 mb-2">
              <FileText className="w-5 h-5 text-blue-600" />
              <h4 className="font-semibold text-blue-900">Certificados de Calibração</h4>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {renderCertificateField('Certificado Caltek', 'certificadoCaltek')}
              {renderCertificateField('Certificado Neomatic', 'certificadoNeomatic')}
              {renderCertificateField('Certificado Tecnocalibração', 'certificadoTecnocalibracao')}
              {renderCertificateField('Certificado Calibratools', 'certificadoCalibratools')}
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start gap-2">
            <FileText className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-blue-800">
              <p className="font-medium">Informações sobre upload</p>
              <ul className="text-blue-700 mt-1 list-disc list-inside space-y-1">
                <li>Apenas arquivos PDF são aceitos</li>
                <li>Tamanho máximo: 5MB por arquivo</li>
                <li>Você pode fazer download dos certificados após o upload</li>
              </ul>
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <Button type="button" variant="outline" onClick={handleClose} className="flex-1">
              Cancelar
            </Button>
            <Button 
              type="submit" 
              className="flex-1 bg-blue-600 hover:bg-blue-700"
              disabled={!selectedInstrument}
            >
              Salvar Certificados
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CertificatesModal;
